import type { FieldValues, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import type { FormValueReader } from 'resources/rate_settings/utils/fieldsVisibility';
import {
  baseMarginField,
  isBlank,
  num,
  solveRates,
  type RateSolveConfig,
} from '../rateController/rateControllerModel';

type SeedRateDefaultsProps = {
  source: string;
  recordValue: FormValueReader;
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  config: RateSolveConfig;
};

export const seedRateDefaults = ({
  source,
  recordValue,
  getValues,
  setValue,
  config,
}: SeedRateDefaultsProps) => {
  const ownBase = recordValue(`${source}.base_charge_rate_cents`);
  const ownMargin = recordValue(baseMarginField(source));
  const ownMultiplier = recordValue(`${source}.blended_rates.blended_rate_markup`);

  const writeIfBlank = (path: string, next: number, treatZeroAsBlank = false) => {
    const current = getValues(path);
    const blank = isBlank(current) || (treatZeroAsBlank && Number(current) === 0);
    if (Number.isFinite(next) && blank) {
      setValue(path, next, { shouldDirty: false });
    }
  };
  const writeProRate = (cents: number) => {
    if (cents > 0) {
      writeIfBlank(`${source}.pro_rate_cents`, cents, true);
    }
  };

  const hasOwnProPair = !isBlank(ownBase) && !isBlank(ownMargin);
  const hasOwnBlendedPair =
    !isBlank(ownBase) && !isBlank(ownMultiplier) && Number.isFinite(Number(ownMultiplier));

  if (hasOwnProPair || hasOwnBlendedPair) {
    const solved = solveRates({
      controlled: ['baseMargin', 'baseChargeRate'],
      values: {
        professionalRate: 0,
        baseMargin: num(ownMargin),
        baseChargeRate: num(ownBase),
        chargeRate: 0,
      },
      markups: config.markups,
      blendedFactor: config.blendedFactor,
      burdenDivisor: config.burdenDivisor,
    });
    if (hasOwnProPair) {
      writeProRate(Math.round(solved.professionalRate));
    }
    if (hasOwnBlendedPair) {
      writeIfBlank(
        `${source}.blended_rate_cents`,
        Math.round(solved.chargeRate * (1 + num(ownMultiplier))),
      );
    }
  }
};
